import { Component, type ErrorInfo, type ReactNode } from 'react';

import { ApiError } from '@/lib/api-client';

interface ErrorBoundaryProps {
  children: ReactNode;
  /** Custom fallback. Receives the error and a reset callback. */
  fallback?: (error: Error, reset: () => void) => ReactNode;
}

interface ErrorBoundaryState {
  error: Error | null;
}

/**
 * App-level render guard.
 *
 * React error boundaries must be class components — there is no hook
 * equivalent. Per-route failures are handled by the router's `errorElement`
 * (see src/routes/RouteError.tsx); this one catches everything above the
 * router, so a crash in the provider tree still renders something a person can
 * read and retry from rather than a white page in front of leadership.
 */
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  override state: ErrorBoundaryState = { error: null };

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    const requestId = error instanceof ApiError ? error.requestId : null;
    // `console.error` is the only client-side error sink until B4 wires OTel.
    // It keeps the requestId that correlates to the backend structlog line.
    console.error('[brain] unhandled render error', {
      message: error.message,
      requestId,
      componentStack: info.componentStack,
    });
  }

  private readonly reset = (): void => {
    this.setState({ error: null });
  };

  override render(): ReactNode {
    const { error } = this.state;
    if (error === null) return this.props.children;
    if (this.props.fallback !== undefined) return this.props.fallback(error, this.reset);

    const requestId = error instanceof ApiError ? error.requestId : null;

    return (
      <div className="flex min-h-screen items-center justify-center bg-canvas p-6">
        <div className="max-w-lg rounded-xl border border-hairline bg-surface p-6 shadow-panel">
          <p className="text-sm font-bold uppercase tracking-kicker text-status-crit">
            Something broke
          </p>
          <h1 className="mt-1 text-lg font-extrabold text-text-primary">
            The dashboard could not render this view
          </h1>
          <p className="mt-2 text-base text-text-secondary">{error.message}</p>
          {requestId === null ? null : (
            <p className="mt-2 font-mono text-2xs text-text-muted">request-id: {requestId}</p>
          )}
          <button
            type="button"
            onClick={this.reset}
            className="mt-4 rounded bg-accent px-3 py-1.5 text-sm font-bold text-text-on-dark"
          >
            Try again
          </button>
        </div>
      </div>
    );
  }
}
